/**
 * EMZI NEXUS V6.3 - Content Studio V2
 * Complete rebuild with 9-step wizard, conditional branching, 10 Knowledge Pack integration
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as api from '../api/contentStudioV2';

// Add global styles for smooth scrolling
const scrollStyles = `
  .content-studio-v2-scrollable {
    scroll-behavior: smooth;
  }
  
  .content-studio-v2-scrollable::-webkit-scrollbar {
    width: 6px;
  }
  
  .content-studio-v2-scrollable::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }
  
  .content-studio-v2-scrollable::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
  
  .content-studio-v2-scrollable::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

// Step Components
import Step0PlatformDuration from './contentV2/steps/Step0PlatformDuration';
import Step1Objective from './contentV2/steps/Step1Objective';
import Step2TargetedAudience from './contentV2/steps/Step2TargetedAudience';
import Step3KnowledgeSelection from './contentV2/steps/Step3KnowledgeSelection';
import Step4SpiderWeb from './contentV2/steps/Step4SpiderWeb';
import Step5EvidenceMode from './contentV2/steps/Step4EvidenceMode';
import Step6ReviewPrime from './contentV2/steps/Step6ReviewPrime';
import Step7SOADrill from './contentV2/steps/Step5SOADrill';
import Step7_5_SOAReview from './contentV2/steps/Step7_5_SOAReview';
import Step8SplitTest from './contentV2/steps/Step6SplitTest';
import Step10FormatSuggestion from './contentV2/steps/Step8FormatSuggestion';
import Step10Storyboard from './contentV2/steps/Step10Storyboard';

const ContentStudioV2 = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [sessionId, setSessionId] = useState(() => {
    // First check URL parameter for session
    const sessionFromUrl = searchParams.get('session');
    if (sessionFromUrl && sessionFromUrl.startsWith('CS_')) {
      console.log('[ContentStudioV2] Using session from URL:', sessionFromUrl);
      return sessionFromUrl;
    }
    
    // Try to restore existing session from localStorage
    const stored = localStorage.getItem('cs_v2_session_id');
    if (stored) {
      console.log('[ContentStudioV2] Restored session from localStorage:', stored);
      return stored;
    }
    
    // Create new session
    const newSession = `CS_${Date.now()}`;
    console.log('[ContentStudioV2] Created new session:', newSession);
    return newSession;
  });
  
  const [currentStep, setCurrentStep] = useState(() => {
    // Check URL parameter first
    const stepFromUrl = searchParams.get('step');
    if (stepFromUrl) {
      const stepNum = parseInt(stepFromUrl);
      if (stepNum >= 0 && stepNum <= 10) {
        return stepNum;
      }
    }
    // Restore current step from localStorage
    const stored = localStorage.getItem(`cs_v2_${sessionId}_step`);
    return stored ? parseInt(stored) : 0; // Start at Step 0 (Platform Selection)
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Edit mode - for editing sessions with 'changes_requested' status
  const [isEditMode, setIsEditMode] = useState(false);
  const [editModeInfo, setEditModeInfo] = useState(null); // Stores supervisor feedback etc.
  
  // Custom modal state for "Start New Session" (replaces window.confirm for sandbox safety)
  const [showNewSessionConfirm, setShowNewSessionConfirm] = useState(false);
  
  // Session data with localStorage persistence
  const [sessionData, setSessionData] = useState(() => {
    // Try to restore from localStorage on mount
    const stored = localStorage.getItem(`cs_v2_${sessionId}_data`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse stored session data:', e);
      }
    }
    return {
      objective: null,
      audience: null,
      knowledgeSpider: null,
      selectedProducts: null,
      selectedServices: null,
      selectedValues: null,
      selectedDirection: null,
      selectedAmbassador: null,
      weights: null,
      evidenceMode: null,
      soaPlan: null,
      narrative: null,
      riskNotes: null,
      soaResults: null,
      soa_answers: null, // SOA Drill answers
      showReview: false, // Flag to show review page
      selectedScript: null,
      platformDuration: null,
      selectedFormat: null,
      submission: null,
      platformContext: null  // New: Channel-first flow
    };
  });

  // Persist session ID on mount
  useEffect(() => {
    localStorage.setItem('cs_v2_session_id', sessionId);
  }, [sessionId]);

  // Validate stored session on mount - clear if already submitted (but NOT changes_requested)
  useEffect(() => {
    const validateStoredSession = async () => {
      const storedSessionId = localStorage.getItem('cs_v2_session_id');
      if (!storedSessionId || !storedSessionId.startsWith('CS_')) return;
      
      // Skip validation if we're explicitly loading a session from URL (edit mode)
      const sessionFromUrl = searchParams.get('session');
      if (sessionFromUrl === storedSessionId) {
        console.log('[ContentStudioV2] Session from URL matches stored, skipping validation');
        return;
      }
      
      try {
        const response = await api.getSessionData(token, storedSessionId);
        // Only clear for pending_approval and approved - NOT changes_requested
        // changes_requested sessions should be editable
        if (response && (
          response.submission_status === 'pending_approval' ||
          response.submission_status === 'approved'
        )) {
          console.log('[ContentStudioV2] Stored session already submitted, clearing localStorage');
          localStorage.removeItem('cs_v2_session_id');
          localStorage.removeItem(`cs_v2_${storedSessionId}_step`);
          localStorage.removeItem(`cs_v2_${storedSessionId}_data`);
          
          // Generate new session ID and reload
          const newSession = `CS_${Date.now()}`;
          setSessionId(newSession);
          setCurrentStep(0);
          setSessionData({
            objective: null,
            audience: null,
            knowledgeSpider: null,
            selectedProducts: null,
            selectedServices: null,
            selectedValues: null,
            selectedDirection: null,
            selectedAmbassador: null,
            weights: null,
            evidenceMode: null,
            soaPlan: null,
            narrative: null,
            riskNotes: null,
            soaResults: null,
            soa_answers: null,
            showReview: false,
            selectedScript: null,
            platformDuration: null,
            selectedFormat: null,
            submission: null,
            platformContext: null
          });
        }
      } catch (error) {
        // Session not found in backend - this is fine for new sessions
        console.log('[ContentStudioV2] Session not found in backend, continuing with local data');
      }
    };
    
    validateStoredSession();
  }, [token, searchParams]);

  // Load session data from backend if resuming an existing session
  useEffect(() => {
    const loadSessionFromBackend = async () => {
      const sessionFromUrl = searchParams.get('session');
      if (sessionFromUrl && sessionFromUrl.startsWith('CS_')) {
        try {
          setLoading(true);
          console.log('[ContentStudioV2] Loading session from backend:', sessionFromUrl);
          const response = await api.getSessionData(token, sessionFromUrl);
          
          if (response) {
            // Handle changes_requested - ALLOW EDITING
            if (response.submission_status === 'changes_requested') {
              console.log('[ContentStudioV2] Session has changes_requested status - enabling edit mode');
              setIsEditMode(true);
              setEditModeInfo({
                status: 'changes_requested',
                supervisorComment: response.supervisor_comment || null,
                submissionId: response.submission_id,
                originalSubmittedAt: response.submitted_at
              });
              
              // Update session ID to match URL
              setSessionId(sessionFromUrl);
              
              // Map backend data to frontend sessionData structure
              setSessionData({
                objective: response.objective_type,
                audience: response.audience,
                knowledgeSpider: response.knowledge_spider,
                selectedProducts: response.selected_packs?.filter(p => p.pack_type === 'product') || [],
                selectedServices: response.selected_packs?.filter(p => p.pack_type === 'service') || [],
                selectedValues: response.selected_packs?.filter(p => p.pack_type === 'values') || [],
                selectedDirection: response.campaign_direction,
                selectedAmbassador: response.ambassador,
                weights: response.knowledge_weights,
                evidenceMode: response.evidence_mode,
                soaPlan: response.soa_prime,
                narrative: response.soa_prime?.narrative,
                riskNotes: response.soa_prime?.risk_notes,
                soaResults: response.soa_summary_v2,
                soa_answers: response.soa_answers_v2,
                showReview: false,
                selectedScript: response.selected_script,
                platformDuration: response.platform_context?.normalized_seconds,
                selectedFormat: response.selected_format,
                submission: response.submission_id ? response : null,
                platformContext: response.platform_context,
                storyboardBeats: response.storyboard_beats,
                scriptVariants: response.script_variants,
                project_name: response.project_name  // Pre-fill project name for edit/resubmit
              });
              console.log('[ContentStudioV2] Edit mode session loaded from backend');
              setLoading(false);
              return;
            }
            
            // For pending_approval and approved - redirect to fresh studio (read-only)
            if (response.submission_status === 'pending_approval' || 
                response.submission_status === 'approved') {
              console.log('[ContentStudioV2] Session is pending_approval/approved, redirecting to My Submissions');
              // Redirect to My Submissions instead of blank studio
              navigate('/my-submissions', { replace: true });
              return;
            }
            
            // For in-progress sessions (no submission status) - normal flow
            // Map backend data to frontend sessionData structure
            setSessionData({
              objective: response.objective_type,
              audience: response.audience,
              knowledgeSpider: response.knowledge_spider,
              selectedProducts: response.selected_packs?.filter(p => p.pack_type === 'product') || [],
              selectedServices: response.selected_packs?.filter(p => p.pack_type === 'service') || [],
              selectedValues: response.selected_packs?.filter(p => p.pack_type === 'values') || [],
              selectedDirection: response.campaign_direction,
              selectedAmbassador: response.ambassador,
              weights: response.knowledge_weights,
              evidenceMode: response.evidence_mode,
              soaPlan: response.soa_prime,
              narrative: response.soa_prime?.narrative,
              riskNotes: response.soa_prime?.risk_notes,
              soaResults: response.soa_summary_v2,
              soa_answers: response.soa_answers_v2,
              showReview: false,
              selectedScript: response.selected_script,
              platformDuration: response.platform_context?.normalized_seconds,
              selectedFormat: response.selected_format,
              submission: response.submission_id ? response : null,
              platformContext: response.platform_context,
              storyboardBeats: response.storyboard_beats,
              scriptVariants: response.script_variants,
              project_name: response.project_name  // Pre-fill project name for edit/resubmit
            });
            console.log('[ContentStudioV2] Session loaded from backend');
          }
        } catch (error) {
          console.error('[ContentStudioV2] Failed to load session from backend:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadSessionFromBackend();
  }, [searchParams, token, navigate]);

  // Watch for URL parameter changes and update currentStep
  useEffect(() => {
    const stepFromUrl = searchParams.get('step');
    if (stepFromUrl) {
      const stepNum = parseInt(stepFromUrl);
      if (stepNum >= 0 && stepNum <= 10 && stepNum !== currentStep) {
        console.log('[ContentStudioV2] URL navigation to step:', stepNum);
        setCurrentStep(stepNum);
      }
    }
  }, [searchParams, currentStep]);

  // Persist sessionData to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(`cs_v2_${sessionId}_data`, JSON.stringify(sessionData));
  }, [sessionData, sessionId]);

  // Persist current step to localStorage
  useEffect(() => {
    localStorage.setItem(`cs_v2_${sessionId}_step`, currentStep.toString());
  }, [currentStep, sessionId]);

  const steps = [
    { num: 0, title: 'Platform & Duration', component: Step0PlatformDuration },
    { num: 1, title: 'Objective', component: Step1Objective },
    { num: 2, title: 'Audience', component: Step2TargetedAudience },
    { num: 3, title: 'Knowledge', component: Step3KnowledgeSelection },
    { num: 4, title: 'Spider Web', component: Step4SpiderWeb },
    { num: 5, title: 'Evidence', component: Step5EvidenceMode },
    { num: 6, title: 'Review & Prime', component: Step6ReviewPrime },
    { num: 7, title: 'SOA Drill', component: Step7SOADrill },
    { num: 8, title: 'Format', component: Step10FormatSuggestion },  // SWAPPED: Now Step 8
    { num: 9, title: 'Script', component: Step8SplitTest },  // SWAPPED: Now Step 9
    { num: 10, title: 'Storyboard', component: Step10Storyboard }
  ];

  const handleStepComplete = (stepNum, data) => {
    console.log(`[ContentStudioV2] Step ${stepNum} completed with data:`, data);
    setSessionData(prev => {
      const updated = { ...prev, ...data };
      console.log('[ContentStudioV2] Updated sessionData:', updated);
      return updated;
    });
    
    // If showReview flag is set, stay on current step to show review page
    if (data?.showReview) {
      console.log('[ContentStudioV2] showReview flag detected, staying on step', stepNum);
      return;
    }
    
    if (stepNum < 10) {
      const nextStep = stepNum + 1;
      setCurrentStep(nextStep);
      // Update URL to reflect the new step (prevents useEffect from resetting)
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('step', nextStep.toString());
        return newParams;
      }, { replace: true });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      // Update URL to reflect the new step
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('step', prevStep.toString());
        return newParams;
      }, { replace: true });
    }
  };
  
  // Special back handler for Step 10 (Storyboard) - should go to Step 8 (Format Selection)
  const handleBackFromStep10 = () => {
    setCurrentStep(8);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('step', '8');
      return newParams;
    }, { replace: true });
  };
  
  const handleBackFromStep8 = () => {
    // Step 8 (Format Selection) should always go back to Step 7.5 (SOA Review)
    // Step 7.5 is rendered when currentStep === 7 AND showReview === true
    setSessionData(prev => ({ ...prev, showReview: true }));
    setCurrentStep(7);
    // Update URL to reflect step 7
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('step', '7');
      return newParams;
    }, { replace: true });
  };

  const clearSession = () => {
    // Clear localStorage
    localStorage.removeItem(`cs_v2_${sessionId}_data`);
    localStorage.removeItem(`cs_v2_${sessionId}_step`);
    localStorage.removeItem('cs_v2_session_id');
    // Generate fresh session
    const newId = `CS_${Date.now()}`;
    // Reset state
    setSessionId(newId);
    setSessionData({
      objective: null,
      audience: null,
      knowledgeSpider: null,
      selectedProducts: null,
      selectedServices: null,
      selectedValues: null,
      selectedDirection: null,
      selectedAmbassador: null,
      weights: null,
      evidenceMode: null,
      soaPlan: null,
      narrative: null,
      riskNotes: null,
      soaResults: null,
      soa_answers: null,
      showReview: false,
      selectedScript: null,
      platformDuration: null,
      selectedFormat: null,
      submission: null,
      platformContext: null
    });
    setCurrentStep(0);
    setError(null);
    setIsEditMode(false);
    setEditModeInfo(null);
    // Clear URL params so step=5 doesn't persist
    setSearchParams({ step: '0' }, { replace: true });
  };

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <>
      {/* Inject scroll styles */}
      <style>{scrollStyles}</style>
      
      {/* New Session Confirmation Modal (replaces window.confirm for sandbox safety) */}
      {showNewSessionConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }} data-testid="new-session-confirm-overlay">
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '24px',
            maxWidth: '400px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>
              Start New Session?
            </div>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '20px', lineHeight: '1.5' }}>
              Current progress will be lost. Are you sure you want to start a new session?
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                data-testid="new-session-cancel-btn"
                onClick={() => setShowNewSessionConfirm(false)}
                style={{
                  flex: 1, padding: '10px', background: '#fff', border: '2px solid #ddd',
                  borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#666', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                data-testid="new-session-confirm-btn"
                onClick={() => {
                  setShowNewSessionConfirm(false);
                  clearSession();
                }}
                style={{
                  flex: 1, padding: '10px', background: '#E53935', border: 'none',
                  borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer'
                }}
              >
                Yes, Start New
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div style={{ 
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #0e67a2 0%, #005BBB 100%)'
      }}>
      {/* Header - Fixed at top */}
      <div style={{
        background: '#0e67a2',
        padding: '16px 20px',
        color: '#fff',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        zIndex: 100
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
              Content Studio V2
              {isEditMode && <span style={{ 
                marginLeft: '10px', 
                fontSize: '12px', 
                background: '#FF9800', 
                padding: '4px 8px', 
                borderRadius: '4px',
                fontWeight: 600
              }}>✏️ EDIT MODE</span>}
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.9 }}>Session: {sessionId}</p>
          </div>
          <button
            data-testid="start-new-session-btn"
            onClick={() => setShowNewSessionConfirm(true)}
            style={{
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
            }}
          >
            🔄 Start New Session
          </button>
        </div>
      </div>

      {/* Edit Mode Banner - Shows supervisor feedback */}
      {isEditMode && editModeInfo && (
        <div style={{
          background: 'linear-gradient(90deg, #FF9800, #F57C00)',
          padding: '12px 20px',
          color: '#fff',
          flexShrink: 0,
          borderBottom: '2px solid #E65100'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>📝</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>
                Changes Requested by Supervisor
              </div>
              {editModeInfo.supervisorComment ? (
                <div style={{ 
                  fontSize: '13px', 
                  opacity: 0.95,
                  background: 'rgba(255,255,255,0.15)',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  marginTop: '4px'
                }}>
                  <span>&ldquo;</span>{editModeInfo.supervisorComment}<span>&rdquo;</span>
                </div>
              ) : (
                <div style={{ fontSize: '13px', opacity: 0.85 }}>
                  Please make the necessary changes and resubmit for approval.
                </div>
              )}
            </div>
            <button
              onClick={() => navigate('/my-submissions')}
              style={{
                padding: '6px 12px',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '4px',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              ← Back to Submissions
            </button>
          </div>
        </div>
      )}

      {/* Progress Bar - Fixed below header */}
      <div style={{ 
        background: '#fff', 
        padding: '12px 20px', 
        borderBottom: '1px solid #eee',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
          {steps.map((step, idx) => (
            <React.Fragment key={step.num}>
              <div style={{
                flex: 1,
                height: '4px',
                background: currentStep >= step.num ? '#0e67a2' : '#E0E0E0',
                borderRadius: '2px',
                transition: 'background 0.3s'
              }} />
              {idx < steps.length - 1 && <div style={{ width: '2px' }} />}
            </React.Fragment>
          ))}
        </div>
        <div style={{ fontSize: '13px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
          <span>Step {currentStep}/{steps.length - 1}: {steps[currentStep].title}</span>
          <span>{Math.round((currentStep / (steps.length - 1)) * 100)}%</span>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div 
        className="content-studio-v2-scrollable"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: '80px',
          position: 'relative'
        }}
      >
        {/* Error Display */}
        {error && (
          <div style={{
            margin: '16px 20px',
            padding: '12px',
            background: '#fee',
            border: '1px solid #c00',
            borderRadius: '8px',
            color: '#c00',
            fontSize: '14px'
          }}>
            <strong>Error:</strong> {error}
            <button 
              onClick={() => setError(null)}
              style={{
                float: 'right',
                background: 'transparent',
                border: 'none',
                color: '#c00',
                cursor: 'pointer',
                fontSize: '18px',
                padding: 0
              }}
            >
              ×
            </button>
            {/* Show recovery button for "Session not found" errors */}
            {(typeof error === 'string' && (error.toLowerCase().includes('session not found') || error.toLowerCase().includes('not found'))) && (
              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #fcc' }}>
                <button
                  data-testid="session-recovery-btn"
                  onClick={() => { setError(null); clearSession(); }}
                  style={{
                    padding: '8px 16px', background: '#0e67a2', border: 'none',
                    borderRadius: '6px', color: '#fff', fontSize: '13px',
                    fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  🔄 Start Fresh Session
                </button>
                <span style={{ marginLeft: '10px', fontSize: '12px', color: '#888' }}>
                  Session expired or not found. Click to start over.
                </span>
              </div>
            )}
          </div>
        )}

        {/* Current Step */}
        <div style={{ padding: '20px' }}>
          {/* Show SOA Review page if flag is set and on step 7 */}
          {currentStep === 7 && sessionData.showReview ? (
            <Step7_5_SOAReview
              sessionId={sessionId}
              token={token}
              sessionData={sessionData}
              onComplete={() => {
                // Clear review flag and proceed to next step
                setSessionData(prev => ({ ...prev, showReview: false }));
                handleStepComplete(currentStep, { showReview: false });
              }}
              onBack={() => {
                // Go back to Prime SOA (Step 6)
                setSessionData(prev => ({ ...prev, showReview: false }));
                setCurrentStep(6);
              }}
              onEditQuestion={(qIndex) => {
                // Go back to SOA Drill at specific question
                console.log('[ContentStudio] Editing question:', qIndex);
                setSessionData(prev => ({ ...prev, showReview: false }));
                // Question index will be handled by SOA Drill resume logic
                navigate(`?step=7&resume=${qIndex}`);
              }}
              setError={setError}
            />
          ) : (
            <CurrentStepComponent
              sessionId={sessionId}
              token={token}
              user={user}
              sessionData={sessionData}
              onComplete={(data) => handleStepComplete(currentStep, data)}
              onBack={currentStep === 10 ? handleBackFromStep10 : (currentStep === 8 ? handleBackFromStep8 : handleBack)}
              setError={setError}
              setLoading={setLoading}
            />
          )}
        </div>
      </div>

        {/* Loading now handled by global DynamicLoader - old overlay removed */}
      </div>
    </>
  );
};

export default ContentStudioV2;